/**
 * blogStorage — Local-only blog post storage.
 *
 * Bridges the static `blog-posts.json` with user-authored drafts saved in
 * `localStorage`. Drafts persist on a single browser only — they do not sync
 * across devices and are invisible to other visitors. For real publishing,
 * paste the JSON into `src/content/blog-posts.json` or enable Lovable Cloud.
 */
import blogPostsData from "@/content/blog-posts.json";

export interface BlogPostFull {
  id: string;
  title: string;
  metaDescription: string;
  category: string;
  date: string;
  readTime: string;
  image: string;
  imageAlt: string;
  tags: string[];
  author: string;
  authorId?: string;
  intro: string;
  sections: Array<{ title: string; content: string }>;
  conclusion: string;
  faq: Array<{ question: string; answer: string }>;
  relatedPosts: string[];
}

const STORAGE_KEY = "itqan_local_blog_posts_v1";

const isBrowser = () => typeof window !== "undefined";

export function getLocalPosts(): Record<string, BlogPostFull> {
  if (!isBrowser()) return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

export function saveLocalPost(post: BlogPostFull): void {
  if (!isBrowser()) return;
  const current = getLocalPosts();
  current[post.id] = post;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(current));
}

export function deleteLocalPost(id: string): void {
  if (!isBrowser()) return;
  const current = getLocalPosts();
  delete current[id];
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(current));
}

export function getAllPosts(): Record<string, BlogPostFull> {
  const staticPosts = blogPostsData as Record<string, BlogPostFull>;
  return { ...staticPosts, ...getLocalPosts() };
}

export function getPost(id: string): BlogPostFull | null {
  const staticPosts = blogPostsData as Record<string, BlogPostFull>;
  if (staticPosts[id]) return staticPosts[id];
  return getLocalPosts()[id] ?? null;
}

export function isLocalPost(id: string): boolean {
  return Object.prototype.hasOwnProperty.call(getLocalPosts(), id);
}
